import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { heroCopy } from "../lib/hero";
import { useStore } from "../lib/store";
import type { CallState, SipLight } from "../lib/types";
import { IconSearch } from "./Icons";
import { StatusPill, cx } from "./ui";

const JUMP: Record<string, string> = {
  dashboard: "/stats",
  stats: "/stats",
  statistics: "/stats",
  home: "/console",
  console: "/console",
  dial: "/console",
  caller: "/console",
  call: "/console",
  originate: "/console",
  leads: "/console",
  history: "/history",
  scripts: "/scripts",
  ivr: "/scripts",
  sip: "/sip",
  admin: "/admin",
  settings: "/settings",
  voice: "/orchestrator",
  orchestrator: "/orchestrator",
  ops: "/orchestrator",
};

const PAGE_TITLE: Record<string, string> = {
  "/console": "Dial",
  "/scripts": "Scripts",
  "/history": "History",
  "/stats": "Stats",
  "/dashboard": "Stats",
  "/settings": "Settings",
  "/sip": "SIP",
  "/admin": "Admin",
  "/campaigns": "Campaigns",
  "/call": "New call",
  "/orchestrator": "Voice ops",
};

function sipTone(status: SipLight, css: string): "teal" | "ok" | "fail" | "live" | "mute" {
  if (css === "sip-ok" || status === "REGED" || status === "NOREG") return "ok";
  if (css === "sip-wait" || status === "FAIL_WAIT") return "live";
  if (status === "UNKNOWN") return "mute";
  return "fail";
}

function isLive(state: CallState): boolean {
  return state !== "IDLE" && state !== "DONE";
}

/**
 * Top ops bar: search, SIP/mode chips, live pulse, operator avatar.
 */
export function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useStore();
  const { sip, sipText, sipCss, mode } = state.snapshot;
  const [query, setQuery] = useState("");
  const [initial, setInitial] = useState("C");

  useEffect(() => {
    let cancelled = false;
    void api
      .settings()
      .then((data) => {
        if (cancelled) return;
        const name = (data.operator_name ?? data.business_name ?? "C").trim();
        setInitial((name[0] || "C").toUpperCase());
      })
      .catch(() => {
        /* avatar fallback stays C */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function onSearch(event: FormEvent) {
    event.preventDefault();
    const raw = query.trim();
    if (!raw) return;
    const key = raw.toLowerCase();
    if (JUMP[key]) {
      navigate(JUMP[key]);
      return;
    }
    navigate(`/history?q=${encodeURIComponent(raw)}`);
  }

  const live = isLive(state.snapshot.state);
  const copy = state.snapshot.heroCopy || heroCopy(state.snapshot.state, state.snapshot.detail);
  const title =
    PAGE_TITLE[location.pathname] ??
    (location.pathname.replace("/", "").replace(/\b\w/g, (ch) => ch.toUpperCase()) || "CDIALER");

  return (
    <header className="flex h-16 shrink-0 items-center gap-4 border-b border-teal/10 bg-navy/60 px-5">
      <div className="min-w-[140px]">
        <div className="text-[11px] uppercase tracking-[0.2em] text-mute">CDIALER</div>
        <div className="text-sm font-medium text-ink">{title}</div>
      </div>
      <form onSubmit={onSearch} className="relative mx-auto hidden w-full max-w-md md:block">
        <IconSearch className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-mute" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search leads, dest, CLI…"
          className="w-full rounded-full bg-canvas py-2 pr-4 pl-10 text-sm text-ink outline-none hairline placeholder:text-mute/70 focus:border-teal/40"
        />
      </form>
      <div className="ml-auto flex items-center gap-2">
        <StatusPill tone={sipTone(sip, sipCss)}>{sipText || `SIP ${sip}`}</StatusPill>
        <StatusPill tone={mode === "live" ? "live" : "mute"}>{mode}</StatusPill>
        {live ? (
          <button
            type="button"
            onClick={() => navigate("/console")}
            className="pill pill-live"
            title={copy}
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-live" />
            LIVE
          </button>
        ) : null}
        {!state.wsOpen && state.authenticated ? (
          <StatusPill tone="mute">ws</StatusPill>
        ) : null}
        <div
          className={cx(
            "ml-1 flex h-9 w-9 items-center justify-center rounded-full bg-teal/15 font-semibold text-teal",
            "ring-1 ring-teal/30",
          )}
          title="Operator"
        >
          {initial}
        </div>
      </div>
    </header>
  );
}
